import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useWishlist from "../hooks/useWishlist";
import useAuth from "../hooks/useAuth";
import useCart from "../hooks/useCart";
import { formatInr, getProductPriceDisplay } from "../utils/pricing";
import "./WishlistPage.css";

const SIZE_NOT_APPLICABLE = 'NA';

const getStockQuantity = (product = {}) => {
  const parsedStockQuantity = Number(product?.stockQuantity);

  return Number.isFinite(parsedStockQuantity)
    ? Math.max(0, Math.floor(parsedStockQuantity))
    : 0;
};

const getStockDisplayLabel = (stockQuantity) =>
  stockQuantity === 1
    ? "Only 1 item left in stock"
    : `Only ${stockQuantity} items left in stock`;

const getSelectableSizes = (product = {}) => (
  Array.isArray(product?.availableSizes)
    ? product.availableSizes
      .map((size) => String(size || '').trim())
      .filter((size) => {
        const normalizedSize = String(size || '').trim().toUpperCase();
        return Boolean(normalizedSize && normalizedSize !== 'NA' && normalizedSize !== 'N/A');
      })
    : []
);

const hasManualSizeOptions = (product = {}) => getSelectableSizes(product).length > 1;

const WishlistPage = () => {
  const { wishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const handleRemoveFromWishlist = (productId) => {
    removeFromWishlist(productId);
  };

  const handleAddToCart = (product) => {
    const stockQuantity = getStockQuantity(product);

    if (stockQuantity <= 0) {
      return;
    }

    const selectableSizes = getSelectableSizes(product);

    if (selectableSizes.length > 1) {
      navigate(`/product/${product._id}`);
      return;
    }

    const success = addToCart(product, 1, selectableSizes[0] || SIZE_NOT_APPLICABLE);
    if (success) {
      // Optionally remove from wishlist after adding to cart
      // removeFromWishlist(product._id);
    }
  };

  const handleContinueShopping = () => {
    navigate("/store");
  };

  if (!user) {
    return null; // Don't render anything while redirecting
  }

  const visibleWishlist = wishlist.filter((item) =>
    getStockQuantity(item) > 0
    && item?.availabilityStatus !== "sold_out"
    && item?.isHidden !== true
  );

  return (
    <div className="wishlist-page-container">
      <h1>My Wishlist</h1>

      {visibleWishlist.length === 0 ? (
        <div className="empty-wishlist">
          <h2>Your wishlist is empty</h2>
          <p>Add items to your wishlist to save them for later</p>
          <button className="continue-shopping" onClick={handleContinueShopping}>
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="wishlist-content">
          <div className="wishlist-items">
            {visibleWishlist.map((item) => {
              const stockQuantity = getStockQuantity(item);
              const isOutOfStock = stockQuantity === 0;
              const priceDisplay = getProductPriceDisplay(item);

              return (
                <div className="wishlist-item" key={item._id}>
                <div className="item-image">
                  <img src={item.images?.[0]} alt={item.title} />
                </div>
                <div className="item-details">
                  <h3 className="item-name">{item.title}</h3>
                  <div className="item-price">
                    <span className="current-price">{formatInr(priceDisplay.customerPrice)}</span>
                    {priceDisplay.hasDiscount ? (
                      <span className="original-price">{formatInr(priceDisplay.sellingPrice)}</span>
                    ) : null}
                    {priceDisplay.hasDiscount ? (
                      <span className="discount-percentage">
                        {priceDisplay.discountPercent}% OFF
                      </span>
                    ) : null}
                  </div>
                  {isOutOfStock ? (
                    <p style={{ margin: "0 0 10px", color: "#b23d3d", fontWeight: 600 }}>Out of stock</p>
                  ) : (
                    <p style={{ margin: "0 0 10px", color: "#9a5d00", fontWeight: 600 }}>
                      {getStockDisplayLabel(stockQuantity)}
                    </p>
                  )}
                  <div className="item-actions">
                    <button
                      className="add-to-cart-btn"
                      onClick={() => handleAddToCart(item)}
                      disabled={isOutOfStock}
                    >
                      {isOutOfStock
                        ? 'Out of Stock'
                        : hasManualSizeOptions(item)
                          ? 'Select Size'
                          : 'Add to Cart'}
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveFromWishlist(item._id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                </div>
              );
            })}
          </div>

          <div className="wishlist-summary">
            <h2>Wishlist Summary</h2>
            <div className="summary-item">
              <span>Total Items:</span>
              <span>{visibleWishlist.length}</span>
            </div>
            <button className="continue-shopping" onClick={handleContinueShopping}>
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
